import { useEffect, useRef } from 'react';
import { useEscHandlerContext } from '../context/EscHandlerContext';

/**
 * Custom hook to register/unregister ESC key handlers
 * 
 * @param {Function} handler - Function to execute when ESC is pressed
 * @param {boolean} isActive - Whether the handler should be active (default: true)
 * @param {string} id - Optional unique identifier (auto-generated if not provided)
 * 
 * @example
 * const handleClose = () => setIsOpen(false);
 * useEscHandler(handleClose, isOpen);
 */
export const useEscHandler = (handler, isActive = true, id = null) => {
  const { registerHandler, unregisterHandler } = useEscHandlerContext();
  const handlerIdRef = useRef(id || `esc-handler-${Math.random().toString(36).substring(2, 11)}`);
  const handlerRef = useRef(handler);

  // Update handler ref when it changes
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Register/unregister handler based on isActive
  useEffect(() => {
    if (isActive && handlerRef.current) {
      registerHandler(handlerIdRef.current, () => {
        handlerRef.current?.();
      });
    } else {
      unregisterHandler(handlerIdRef.current);
    }

    // Cleanup on unmount or when isActive becomes false
    return () => {
      unregisterHandler(handlerIdRef.current);
    };
  }, [isActive, registerHandler, unregisterHandler]);

  return handlerIdRef.current;
};

export default useEscHandler;

